import React, { useState, useEffect, useCallback } from 'react';
import { kegsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './KegTracker.css';

const STATUSES = ['all', 'available', 'in_use', 'kicked'];

const EMPTY_FORM = {
  flavor: '',
  size_gallons: 5,
  status: 'available',
  location: 'cc_cooler',
  current_weight: '',
  notes: '',
};

const LOCATIONS = [
  { value: 'cc_cooler', label: 'CC Cooler' },
  { value: 'farm_store', label: 'Farm Store' },
  { value: 'in_transit', label: 'In Transit' },
];

function statusPill(status) {
  const classes = { available: 'pill-green', in_use: 'pill-orange', kicked: 'pill-inactive' };
  const labels = { available: 'Available', in_use: 'In Use', kicked: 'Kicked' };
  return <span className={`pill ${classes[status] || ''}`}>{labels[status] || status}</span>;
}

export default function KegTracker() {
  const [kegs, setKegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await kegsApi.getAll();
      setKegs(data.kegs || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() { setForm(EMPTY_FORM); setEditing({}); }
  function openEdit(keg) { setForm({ ...keg }); setEditing(keg); }
  function closeForm() { setEditing(null); setForm(EMPTY_FORM); }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) { await kegsApi.update(form); }
      else { await kegsApi.create(form); }
      await load();
      closeForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function markKicked(keg) {
    try {
      await kegsApi.update({ ...keg, status: 'kicked' });
      setKegs(ks => ks.map(k => k.id === keg.id ? { ...k, status: 'kicked' } : k));
    } catch (err) {
      setError(err.message);
    }
  }

  const filtered = kegs.filter(k => filter === 'all' || k.status === filter);
  const available = kegs.filter(k => k.status === 'available').length;
  const inUse = kegs.filter(k => k.status === 'in_use').length;

  return (
    <div className="page">
      <AdminPageHeader
        title="Keg Tracker"
        subtitle={`${available} available, ${inUse} in use`}
        action={<button className="btn-primary" onClick={openNew}>+ Add Keg</button>}
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        <div className="filter-bar">
          {STATUSES.map(s => (
            <button key={s} className={`filter-btn ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
              {s === 'all' ? 'All' : s === 'in_use' ? 'In Use' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-state">Loading kegs...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            {kegs.length === 0 ? 'No kegs yet. Add your first keg to get started.' : 'No kegs match this filter.'}
          </div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Flavor</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Current Weight</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(keg => (
                  <tr key={keg.id} className={keg.status === 'kicked' ? 'row-inactive' : ''}>
                    <td className="td-name">{keg.flavor}</td>
                    <td>{keg.size_gallons} gal</td>
                    <td>{statusPill(keg.status)}</td>
                    <td>{LOCATIONS.find(l => l.value === keg.location)?.label || keg.location}</td>
                    <td className="td-price">{keg.current_weight ? `${keg.current_weight} lbs` : '—'}</td>
                    <td className="td-cat">{keg.notes || '—'}</td>
                    <td className="td-actions">
                      <button className="btn-edit" onClick={() => openEdit(keg)}>Edit</button>
                      {keg.status !== 'kicked' && (
                        <button className="btn-kick" onClick={() => markKicked(keg)}>Kick</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing !== null && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing?.id ? 'Edit Keg' : 'Add Keg'}</h2>
              <button className="modal-close" onClick={closeForm}>x</button>
            </div>
            <form onSubmit={handleSave} className="modal-form">
              <div className="field">
                <label>Flavor *</label>
                <input name="flavor" value={form.flavor} onChange={handleChange} required placeholder="e.g. Hibiscus Kombucha" autoFocus />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Size (gallons)</label>
                  <input name="size_gallons" type="number" value={form.size_gallons} onChange={handleChange} min="1" max="15" />
                </div>
                <div className="field">
                  <label>Current Weight (lbs)</label>
                  <input name="current_weight" type="number" step="0.1" value={form.current_weight} onChange={handleChange} placeholder="55.0" />
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Status</label>
                  <select name="status" value={form.status} onChange={handleChange}>
                    <option value="available">Available</option>
                    <option value="in_use">In Use</option>
                    <option value="kicked">Kicked</option>
                  </select>
                </div>
                <div className="field">
                  <label>Location</label>
                  <select name="location" value={form.location} onChange={handleChange}>
                    {LOCATIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Notes</label>
                <input name="notes" value={form.notes} onChange={handleChange} placeholder="Any notes..." />
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing?.id ? 'Save Changes' : 'Add Keg'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
